import { useState, useEffect, useRef } from "react";
import { MapPin, LocateFixed, Loader2 } from "lucide-react";

// Official Dutch government geocoding (PDOK Locatieserver) — free, no key, real
// Dutch place names, CORS-enabled for direct browser use.
const SUGGEST = "https://api.pdok.nl/bzk/locatieserver/search/v3_1/suggest";
const REVERSE = "https://api.pdok.nl/bzk/locatieserver/search/v3_1/reverse";

function cleanName(weergavenaam) {
  return (weergavenaam || "").split(",")[0].trim();
}

// Debounced PDOK place-name suggestions for a given input value. skipRef lets the
// caller suppress the next fetch (e.g. right after selecting a suggestion).
function usePlaceSuggestions(value, skipRef) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (skipRef.current) { skipRef.current = false; return; }
    const q = (value || "").trim();
    if (q.length < 2) { setItems([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`${SUGGEST}?q=${encodeURIComponent(q)}&fq=type:woonplaats&rows=6`);
        const data = await res.json();
        const docs = data?.response?.docs || [];
        setItems([...new Set(docs.map((d) => cleanName(d.weergavenaam)).filter(Boolean))]);
      } catch { setItems([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [value, skipRef]);
  return { items, setItems, loading };
}

export function CityAutocomplete({ value, onChange, inputCls, testid = "ad-city" }) {
  const [open, setOpen] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const boxRef = useRef(null);
  const skipRef = useRef(false);
  const { items, setItems, loading } = usePlaceSuggestions(value, skipRef);

  // Open the dropdown whenever fresh suggestions arrive.
  useEffect(() => { if (items.length) setOpen(true); }, [items]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const select = (name) => { skipRef.current = true; onChange(name); setItems([]); setOpen(false); };

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const res = await fetch(`${REVERSE}?lat=${latitude}&lon=${longitude}&rows=1&type=woonplaats`);
          const data = await res.json();
          const doc = data?.response?.docs?.[0];
          if (doc) select(cleanName(doc.weergavenaam));
        } finally { setGeoLoading(false); }
      },
      () => setGeoLoading(false),
      { timeout: 8000 },
    );
  };

  return (
    <div ref={boxRef} className="relative">
      <div className="relative">
        <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#777]" />
        <input
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => items.length > 0 && setOpen(true)}
          placeholder="Bijv. Utrecht"
          autoComplete="off"
          enterKeyHint="done"
          className={`${inputCls} pl-9 pr-28`}
        />
        <button
          type="button"
          onClick={useMyLocation}
          data-testid="ad-city-geo-btn"
          className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1 rounded-full border border-[#333] bg-[#1c1c1c] px-2.5 py-1 text-xs text-[#A0A0A0] hover:border-[#FF6A00] hover:text-[#FF6A00]"
        >
          {geoLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LocateFixed className="h-3.5 w-3.5" />} Mijn locatie
        </button>
      </div>
      {open && (items.length > 0 || loading) && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-lg border border-[#333] bg-[#1c1c1c] shadow-xl" data-testid="ad-city-suggestions">
          {loading && items.length === 0 ? (
            <div className="px-3 py-2 text-sm text-[#777]">Zoeken…</div>
          ) : (
            items.map((name, i) => (
              <button
                key={`${name}-${i}`}
                type="button"
                data-testid={`ad-city-suggestion-${i}`}
                onClick={() => select(name)}
                className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-[#F5F5F5] hover:bg-[#2A2A2A]"
              >
                <MapPin className="h-3.5 w-3.5 text-[#FF6A00]" /> {name}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
